import { useMemo, useState } from "react";
import { FileText, StickyNote } from "lucide-react";
import { useAppData } from "@/contexts/AppDataContext";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { toast } from "@/hooks/use-toast";
import { brandLearningApi } from "@/resources/brand-learning";
import { brandContextDocumentApi } from "@/resources/brand-context-document";
import { BrandContextDocument, BrandLearning } from "@/types/brand-context";
import { LearningDialog } from "@/components/brand-context/LearningDialog";
import { DocumentDialog } from "@/components/brand-context/DocumentDialog";
import { EntryViewDialog } from "@/components/brand-context/EntryViewDialog";
import { BrandProhibitedKeywordsPanel } from "@/components/brand-context/BrandProhibitedKeywordsPanel";
import { AddEntryButtons } from "@/components/brand-context/AddEntryButtons";
import { EntryFilterBar } from "@/components/brand-context/EntryFilterBar";
import { EntryList } from "@/components/brand-context/EntryList";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { PageTitle } from "@/components/shared/PageTitle";
import { BrandContextEntry, EntryTypeFilter, filterEntries, toEntries } from "@/components/brand-context/entry";

const BrandContext = () => {
  const readOnly = useViewerReadOnly();
  const { selectedBrandId, brands } = useAppData();
  const brandName = brands.find((b) => b.id === selectedBrandId)?.name ?? "this brand";

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<EntryTypeFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const [learningDialogOpen, setLearningDialogOpen] = useState(false);
  const [editingLearning, setEditingLearning] = useState<BrandLearning | null>(null);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<BrandContextDocument | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<BrandContextEntry | null>(null);
  const [viewingEntry, setViewingEntry] = useState<BrandContextEntry | null>(null);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<string | null>(null);

  const { data: learnings = [], isLoading: isLoadingLearnings } = brandLearningApi.useGetBrandLearnings(selectedBrandId);
  const { data: documents = [], isLoading: isLoadingDocuments } = brandContextDocumentApi.useGetBrandContextDocuments(selectedBrandId);

  const createLearning = brandLearningApi.useCreateBrandLearning();
  const updateLearning = brandLearningApi.useUpdateBrandLearning();
  const deleteLearning = brandLearningApi.useDeleteBrandLearning();

  const uploadDocument = brandContextDocumentApi.useUploadBrandContextDocument();
  const updateDocument = brandContextDocumentApi.useUpdateBrandContextDocument();
  const deleteDocument = brandContextDocumentApi.useDeleteBrandContextDocument();
  const downloadDocument = brandContextDocumentApi.useDownloadBrandContextDocument();

  const isLoading = isLoadingLearnings || isLoadingDocuments;
  const entries = useMemo(() => toEntries(learnings, documents), [learnings, documents]);
  const filteredEntries = useMemo(
    () => filterEntries(entries, search, typeFilter, categoryFilter),
    [entries, search, typeFilter, categoryFilter],
  );

  const openAddLearning = () => {
    setEditingLearning(null);
    setLearningDialogOpen(true);
  };

  const openAddDocument = () => {
    setEditingDocument(null);
    setDocumentDialogOpen(true);
  };

  const handleOpenEntry = (entry: BrandContextEntry) => {
    setViewingEntry(entry);
  };

  const handleEditFromView = () => {
    if (!viewingEntry) return;
    const entry = viewingEntry;
    setViewingEntry(null);
    if (entry.type === "learning") {
      const learning = learnings.find((l) => l.id === entry.id);
      if (learning) {
        setEditingLearning(learning);
        setLearningDialogOpen(true);
      }
    } else {
      const document = documents.find((d) => d.id === entry.id);
      if (document) {
        setEditingDocument(document);
        setDocumentDialogOpen(true);
      }
    }
  };

  const handleDownloadEntry = (entry: Extract<BrandContextEntry, { type: "document" }>) => {
    if (!selectedBrandId) return;
    setDownloadingDocumentId(entry.id);
    downloadDocument.mutate(
      { brandId: selectedBrandId, documentId: entry.id, fileName: entry.fileName, mimeType: entry.mimeType },
      { onSettled: () => setDownloadingDocumentId(null) },
    );
  };

  const handleLearningSubmit = (input: { title: string; body: string; category: string; tags: string[] }) => {
    if (!selectedBrandId) return;

    if (editingLearning) {
      updateLearning.mutate(
        { brandId: selectedBrandId, learningId: editingLearning.id, ...input },
        {
          onSuccess: () => {
            toast({ title: "Learning updated" });
            setLearningDialogOpen(false);
          },
        },
      );
    } else {
      createLearning.mutate(
        { brandId: selectedBrandId, ...input },
        {
          onSuccess: () => {
            toast({ title: "Learning added" });
            setLearningDialogOpen(false);
          },
        },
      );
    }
  };

  const handleDocumentCreate = (input: { file: File; title: string; category: string; tags: string[] }) => {
    if (!selectedBrandId) return;
    uploadDocument.mutate(
      { brandId: selectedBrandId, ...input },
      {
        onSuccess: () => {
          toast({ title: "Document uploaded", description: "Signal AI is extracting its text now." });
          setDocumentDialogOpen(false);
        },
      },
    );
  };

  const handleDocumentUpdate = (input: { title: string; category: string; tags: string[] }) => {
    if (!selectedBrandId || !editingDocument) return;
    updateDocument.mutate(
      { brandId: selectedBrandId, documentId: editingDocument.id, ...input },
      {
        onSuccess: () => {
          toast({ title: "Document updated" });
          setDocumentDialogOpen(false);
        },
      },
    );
  };

  const handleDeleteConfirm = () => {
    if (!selectedBrandId || !entryToDelete) return;

    if (entryToDelete.type === "learning") {
      deleteLearning.mutate(
        { brandId: selectedBrandId, learningId: entryToDelete.id },
        {
          onSuccess: () => toast({ title: "Entry removed." }),
          onSettled: () => setEntryToDelete(null),
        },
      );
    } else {
      deleteDocument.mutate(
        { brandId: selectedBrandId, documentId: entryToDelete.id },
        {
          onSuccess: () => toast({ title: "Entry removed." }),
          onSettled: () => setEntryToDelete(null),
        },
      );
    }
  };

  const documentCount = entries.filter((e) => e.type === "document").length;
  const learningCount = entries.filter((e) => e.type === "learning").length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <PageTitle title="Brand Context" brandScoped />
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            A living knowledge base for {brandName}. Upload brand voice docs and capture human
            learnings so Signal AI can apply them across every recommendation, scorecard, and A/B
            test.
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 shrink-0">
            <AddEntryButtons onAddLearning={openAddLearning} onAddDocument={openAddDocument} />
          </div>
        )}
      </div>

      {selectedBrandId && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <FileText className="h-4 w-4" />
            <span className="font-semibold text-foreground">{documentCount}</span> Document{documentCount !== 1 ? "s" : ""}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <StickyNote className="h-4 w-4" />
            <span className="font-semibold text-foreground">{learningCount}</span> Learning{learningCount !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {!selectedBrandId ? (
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
          Select a brand to view its brand context.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
          <div className="space-y-4 min-w-0">
            <EntryFilterBar
              search={search}
              onSearchChange={setSearch}
              typeFilter={typeFilter}
              onTypeFilterChange={setTypeFilter}
              categoryFilter={categoryFilter}
              onCategoryFilterChange={setCategoryFilter}
            />

            <EntryList
              isLoading={isLoading}
              entries={entries}
              filteredEntries={filteredEntries}
              readOnly={readOnly}
              onAddLearning={openAddLearning}
              onAddDocument={openAddDocument}
              onOpenEntry={handleOpenEntry}
              onDeleteEntry={setEntryToDelete}
              onDownloadEntry={handleDownloadEntry}
              downloadingDocumentId={downloadingDocumentId}
            />
          </div>

          <aside className="space-y-6 lg:sticky lg:top-6">
            <BrandProhibitedKeywordsPanel brandId={selectedBrandId} />
          </aside>
        </div>
      )}

      <LearningDialog
        open={learningDialogOpen}
        onOpenChange={setLearningDialogOpen}
        learning={editingLearning}
        isPending={createLearning.isPending || updateLearning.isPending}
        onSubmit={handleLearningSubmit}
      />

      <DocumentDialog
        open={documentDialogOpen}
        onOpenChange={setDocumentDialogOpen}
        existingDocument={editingDocument}
        isPending={uploadDocument.isPending || updateDocument.isPending}
        onSubmitCreate={handleDocumentCreate}
        onSubmitUpdate={handleDocumentUpdate}
      />

      <EntryViewDialog
        open={!!viewingEntry}
        onOpenChange={(o) => { if (!o) setViewingEntry(null); }}
        entry={viewingEntry}
        canEdit={!readOnly}
        onEdit={handleEditFromView}
        onDownload={() => {
          if (viewingEntry?.type === "document") handleDownloadEntry(viewingEntry);
        }}
        isDownloading={viewingEntry?.type === "document" && downloadingDocumentId === viewingEntry.id}
      />

      <ConfirmDeleteDialog
        open={!!entryToDelete}
        onOpenChange={(o) => { if (!o) setEntryToDelete(null); }}
        title="Remove this entry?"
        description="This will permanently remove it from the brand's knowledge base. This action cannot be undone."
        confirmLabel="Remove"
        isPending={deleteLearning.isPending || deleteDocument.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default BrandContext;
